/**
 * Platform (Super Admin) Axios client — completely separate from product api/http.ts.
 * - baseURL: /platform/api/v1
 * - Injects platform JWT; refresh-once on 401; uses platform token storage keys only.
 */

import axios, {
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export const platformHttp = axios.create({
  baseURL: `${BASE_URL}/platform/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// ── Token in memory (set by PlatformAuthContext) ─────────────────────────────

let _platformAccessToken: string | null = null;

export function setPlatformAccessToken(token: string | null): void {
  _platformAccessToken = token;
}

export function getPlatformAccessToken(): string | null {
  return _platformAccessToken;
}

// ── Refresh queue (isolated from product) ───────────────────────────────────

let platformRefreshing = false;
const platformPendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processPlatformQueue(token: string | null, err: unknown): void {
  platformPendingQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(err);
  });
  platformPendingQueue.length = 0;
}

// ── Callbacks (set by PlatformAuthContext) ──────────────────────────────────

let _onPlatformRefresh: (() => Promise<string>) | null = null;
let _onPlatformLogout: (() => void) | null = null;

export function registerPlatformHttpCallbacks(
  onRefresh: () => Promise<string>,
  onLogout: () => void,
): void {
  _onPlatformRefresh = onRefresh;
  _onPlatformLogout = onLogout;
}

// ── Request: attach platform JWT ───────────────────────────────────────────

platformHttp.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_platformAccessToken) {
    config.headers.Authorization = `Bearer ${_platformAccessToken}`;
  }
  return config;
});

// ── Response: refresh-once on 401 ───────────────────────────────────────────

platformHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _platformRetried?: boolean;
    };

    const is401 = error.response?.status === 401;
    const isPlatformAuth =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh');

    if (
      !is401 ||
      originalRequest._platformRetried ||
      isPlatformAuth ||
      !_onPlatformRefresh
    ) {
      return Promise.reject(error);
    }

    if (platformRefreshing) {
      return new Promise<string>((resolve, reject) => {
        platformPendingQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return platformHttp(originalRequest);
      });
    }

    originalRequest._platformRetried = true;
    platformRefreshing = true;

    try {
      const newToken = await _onPlatformRefresh();
      processPlatformQueue(newToken, null);
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`,
      };
      return platformHttp(originalRequest);
    } catch (refreshError) {
      processPlatformQueue(null, refreshError);
      _onPlatformLogout?.();
      return Promise.reject(refreshError);
    } finally {
      platformRefreshing = false;
    }
  },
);
